import _, { parseInt } from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import ChannelAccountsGql from '@/extensions/communication/store/graphql/queries/ChannelAccounts.gql';
import ConnectionsGql from '@/extensions/communication/store/graphql/queries/Connections.gql';
import SaveChannelAccountGql from '@/extensions/communication/store/graphql/mutations/SaveChannelAccount.gql';
import ValidateChannelAccountConnectionGql from '@/extensions/communication/store/graphql/mutations/ValidateChannelAccountConnection.gql';
import DeleteChannelAccountGql from '@/extensions/communication/store/graphql/mutations/DeleteChannelAccount.gql';
import ChannelAccountOAuthUrlGql from '@/extensions/communication/store/graphql/queries/ChannelAccountOAuthUrl.gql';
import WhatsAppSignupConfigGql from '@/extensions/communication/store/graphql/queries/WhatsAppSignupConfig.gql';
import ConnectWhatsAppEmbeddedSignupGql from '@/extensions/communication/store/graphql/mutations/ConnectWhatsAppEmbeddedSignup.gql';
import PointsOfSaleOptionsGql from '@/extensions/communication/store/graphql/queries/PointsOfSaleOptions.gql';

export default {
    // Lightweight {id, name} list of the shop's points of sale — used to populate the
    // POS multi-select that links a connection (mailbox / WhatsApp account) to them.
    fetchPointsOfSaleOptions: shopID =>
        new Promise((resolve, reject) => {
            const variables = {};
            if (shopID) variables.shop = parseInt(shopID);

            apolloClient
                .query({ query: PointsOfSaleOptionsGql, variables, fetchPolicy: 'network-only' })
                .then(({ data }) => resolve(data.EX_POS_PointsOfSale || []))
                .catch(err => reject(err));
        }),

    fetchList: (cmsID, pointOfSaleID = null) =>
        new Promise((resolve, reject) => {
            const variables = { cms_id: parseInt(cmsID) };
            if (pointOfSaleID) variables.point_of_sale_id = parseInt(pointOfSaleID);

            apolloClient
                .query({ query: ChannelAccountsGql, variables, fetchPolicy: 'network-only' })
                .then(({ data }) => resolve(data.EX_COMM_ChannelAccounts))
                .catch(err => reject(err));
        }),

    // Normalized connections (email mailboxes + messenger accounts) for the inbox chips.
    // Passing a POS id returns the shop-level connections plus those linked to that POS.
    fetchConnections: (cmsID, pointOfSaleID = null) =>
        new Promise((resolve, reject) => {
            const variables = { cms_id: parseInt(cmsID) };
            if (pointOfSaleID) variables.point_of_sale_id = parseInt(pointOfSaleID);

            apolloClient
                .query({ query: ConnectionsGql, variables, fetchPolicy: 'network-only' })
                .then(({ data }) => resolve(data.EX_COMM_Connections))
                .catch(err => reject(err));
        }),

    save: (cmsID, account) => {
        if (account === undefined) throw new Error('required argument is missing');

        account = _.removeDeepByKey(_.cloneDeep(account), '__typename');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SaveChannelAccountGql,
                    variables: { cms_id: parseInt(cmsID), account }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_SaveChannelAccount);
                })
                .catch(err => reject(err));
        });
    },

    validate: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ValidateChannelAccountConnectionGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_ValidateChannelAccountConnection);
                })
                .catch(err => reject(err));
        }),

    delete: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteChannelAccountGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_DeleteChannelAccount);
                })
                .catch(err => reject(err));
        }),

    oauthUrl: id =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ChannelAccountOAuthUrlGql,
                    fetchPolicy: 'network-only',
                    variables: { id: parseInt(id) }
                })
                .then(({ data }) => resolve(data.EX_COMM_ChannelAccountOAuthUrl))
                .catch(err => reject(err));
        }),

    // Non-secret config the browser needs to launch Meta Embedded Signup (FB SDK).
    whatsappSignupConfig: cmsID =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: WhatsAppSignupConfigGql,
                    fetchPolicy: 'network-only',
                    variables: { cms_id: parseInt(cmsID) }
                })
                .then(({ data }) => resolve(data.EX_COMM_WhatsAppSignupConfig))
                .catch(err => reject(err));
        }),

    // Finalize a WhatsApp connection from the Embedded Signup result (code + ids).
    connectWhatsApp: (cmsID, payload) =>
        new Promise((resolve, reject) => {
            const variables = {
                cms_id: parseInt(cmsID),
                code: payload.code,
                phone_number_id: payload.phone_number_id,
                waba_id: payload.waba_id
            };
            if (payload.point_of_sale_id) variables.point_of_sale_id = parseInt(payload.point_of_sale_id);
            if (payload.name) variables.name = payload.name;

            apolloClient
                .mutate({ mutation: ConnectWhatsAppEmbeddedSignupGql, variables })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.EX_COMM_ConnectWhatsAppEmbeddedSignup);
                })
                .catch(err => reject(err));
        })
};
